import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { format } from 'date-fns';

const ChatDetailModal = ({ chatId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChatDetail();
  }, [chatId]);

  const loadChatDetail = async () => {
    try {
      const response = await analyticsAPI.getChatDetail(chatId);
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading chat detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
          <div className="loading">Caricamento...</div>
        </div>
      </div>
    );
  }

  const { chat, messages } = data;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <h2>Dettagli Conversazione</h2>

        {/* Info chat */}
        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
            <div><strong>Session ID:</strong> <code>{chat.session_id}</code></div>
            <div><strong>Prodotto:</strong> {chat.product_name}</div>
            <div><strong>Interfaccia:</strong> {chat.interface_type}</div>
            <div><strong>Data:</strong> {format(new Date(chat.created_at), 'dd/MM/yyyy HH:mm:ss')}</div>
            <div><strong>Messaggi:</strong> {formatNumber(chat.total_messages)}</div>
            <div><strong>Token:</strong> {formatNumber(chat.total_tokens)}</div>
            <div><strong>Costo:</strong> {formatCurrency(chat.total_cost_usd)}</div>
            <div><strong>Risparmio:</strong> {formatCurrency(chat.total_savings_usd)}</div>
            <div><strong>Cache Hit Rate:</strong> {
              chat.total_cache_hit_tokens && chat.total_cache_miss_tokens
                ? ((chat.total_cache_hit_tokens / (chat.total_cache_hit_tokens + chat.total_cache_miss_tokens)) * 100).toFixed(2) + '%'
                : 'N/A'
            }</div>
            {chat.ip_address && <div><strong>IP:</strong> {chat.ip_address}</div>}
          </div>
        </div>

        {/* Messaggi */}
        <h3 style={{ marginBottom: '15px' }}>Messaggi ({messages.length})</h3>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {messages.map((msg) => (
            <div
              key={msg.message_id}
              style={{
                marginBottom: '15px',
                padding: '15px',
                borderRadius: '8px',
                background: msg.message_type === 'user' ? '#e3f2fd' : '#f3e5f5',
                border: msg.has_error ? '2px solid #dc3545' : 'none'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <strong style={{ color: msg.message_type === 'user' ? '#1976d2' : '#7b1fa2' }}>
                  {msg.message_type === 'user' ? '👤 Utente' : '🤖 Bot'}
                </strong>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {format(new Date(msg.created_at), 'HH:mm:ss')}
                </span>
              </div>
              
              <div style={{ whiteSpace: 'pre-wrap', marginBottom: '10px' }}>
                {msg.message_content}
              </div>

              {msg.message_type === 'bot' && (
                <>
                  <div style={{ fontSize: '12px', color: '#666', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd' }}>
                    {msg.response_time_seconds && (
                      <div style={{ color: '#667eea', fontWeight: 'bold' }}>
                        <strong>⏱️ Tempo Totale:</strong> {msg.response_time_seconds}s
                      </div>
                    )}
                    {msg.timing_api_call && (
                      <div style={{ color: '#667eea' }}>
                        <strong>⏱️ API Call:</strong> {msg.timing_api_call}s
                      </div>
                    )}
                    {msg.timing_retrieval !== null && msg.timing_retrieval > 0 && (
                      <div style={{ color: '#667eea' }}>
                        <strong>⏱️ Retrieval:</strong> {msg.timing_retrieval}s
                      </div>
                    )}
                    {msg.total_tokens && (
                      <div>
                        <strong>Token:</strong> {formatNumber(msg.total_tokens)}
                        {msg.prompt_tokens && ` (${formatNumber(msg.prompt_tokens)} + ${formatNumber(msg.completion_tokens)})`}
                      </div>
                    )}
                    {msg.total_cost_usd && (
                      <div>
                        <strong>Costo:</strong> {formatCurrency(msg.total_cost_usd)}
                      </div>
                    )}
                    {msg.cache_hit_percentage !== null && (
                      <div>
                        <strong>Cache Hit:</strong> {msg.cache_hit_percentage}%
                      </div>
                    )}
                    {msg.savings_amount_usd && (
                      <div style={{ color: '#28a745' }}>
                        <strong>Risparmio:</strong> {formatCurrency(msg.savings_amount_usd)}
                      </div>
                    )}
                    {msg.has_citations && (
                      <div>
                        <strong>Citazioni:</strong> {msg.citations_count}
                      </div>
                    )}
                  </div>

                  {/* Timing breakdown details */}
                  {(msg.timing_validation || msg.timing_content_loading || msg.timing_system_instruction || msg.timing_message_preparation || msg.timing_post_processing) && (
                    <details style={{ marginTop: '10px', fontSize: '11px', color: '#888' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                        📊 Dettaglio Timing
                      </summary>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '8px', paddingLeft: '10px' }}>
                        {msg.timing_validation !== null && (
                          <div><strong>Validation:</strong> {msg.timing_validation}s</div>
                        )}
                        {msg.timing_content_loading !== null && (
                          <div><strong>Content Loading:</strong> {msg.timing_content_loading}s</div>
                        )}
                        {msg.timing_system_instruction !== null && (
                          <div><strong>System Instruction:</strong> {msg.timing_system_instruction}s</div>
                        )}
                        {msg.timing_message_preparation !== null && (
                          <div><strong>Message Prep:</strong> {msg.timing_message_preparation}s</div>
                        )}
                        {msg.timing_post_processing !== null && (
                          <div><strong>Post Processing:</strong> {msg.timing_post_processing}s</div>
                        )}
                      </div>
                    </details>
                  )}
                </>
              )}

              {msg.has_error && (
                <div style={{ marginTop: '10px', padding: '10px', background: '#f8d7da', borderRadius: '5px', color: '#721c24' }}>
                  <strong>⚠️ Errore:</strong> {msg.error_message}
                </div>
              )}

              {msg.has_citations && msg.citations_json && (
                <details style={{ marginTop: '10px', fontSize: '12px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    Mostra citazioni ({msg.citations_count})
                  </summary>
                  <pre style={{ marginTop: '10px', padding: '10px', background: '#fff', borderRadius: '5px', overflow: 'auto' }}>
                    {JSON.stringify(JSON.parse(msg.citations_json), null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatDetailModal;

